import { InternalConvexClient } from "@convex-dev/browser";
import { useEffect, useState } from "react";
import { convexToJson } from "@convex-dev/common";
import ReactDOM from "react-dom";
// Re-export everything from "@convex-dev/browser"
export * from "@convex-dev/browser";
/**
 * Immutable mutation builder.
 */
class MutationImpl {
    constructor(name, sync, optimisticUpdate = null) {
        this.name = name;
        this.sync = sync;
        this.optimisticUpdate = optimisticUpdate;
    }
    call(...args) {
        return this.sync.mutate(this.name, args, this.optimisticUpdate);
    }
    withOptimisticUpdate(optimisticUpdate) {
        if (this.optimisticUpdate !== null) {
            throw new Error(`Already specified optimistic update for mutation ${this.name}`);
        }
        return new MutationImpl(this.name, this.sync, optimisticUpdate);
    }
}
/** @public */
export class ReactClient {
    constructor(address) {
        this.address = address;
        this.listeners = new Map();
    }
    /**
     * Lazily instantiate the `InternalConvexClient` so we don't create the WebSocket
     * when server-side rendering.
     */
    get sync() {
        if (this.cachedSync) {
            return this.cachedSync;
        }
        this.cachedSync = new InternalConvexClient(this.address, (updates) => this.transition(updates));
        if (this.adminAuth) {
            this.cachedSync.setAdminAuth(this.adminAuth);
        }
        return this.cachedSync;
    }
    /**
     * Set the authentication token to be used for subsequent queries and mutations.
     * Should be called whenever the token changes (i.e. due to expiration and refresh)
     * @param value - JWT-encoded OpenID Connect Identity Token
     */
    setAuth(token) {
        this.sync.setAuth(token);
    }
    /**
     * Clear the current authentication token if set.
     */
    clearAuth() {
        this.sync.clearAuth();
    }
    /**
     * @internal
     */
    setAdminAuth(token) {
        this.adminAuth = token;
        if (this.cachedSync) {
            this.sync.setAdminAuth(token);
        }
    }
    /**
     * Data bind the current React component to a query in one step. You can also use {@link ReactClient.query}
     * to get a {@link ReactQuery} object, which then has the `useSubscription` method.
     *
     * @param name - The name of the query function.
     * @param args - The arguments to the query function.
     * @returns `undefined` if loading and the query's return value otherwise.
     */
    useQuery(name, args) {
        return this.query(name).useSubscription(...args);
    }
    /**
     * Construct a new handle to a `ReactQuery`.
     *
     * @param name - The name of the query function.
     * @returns The `ReactQuery` object with that name.
     */
    query(name) {
        return {
            useSubscription: (...args) => {
                const [state, setState] = useState(undefined);
                useEffect(() => {
                    const { queryToken, unsubscribe } = this.sync.subscribe(name, args);
                    const currentListeners = this.listeners.get(queryToken);
                    if (currentListeners !== undefined) {
                        currentListeners.add(setState);
                    }
                    else {
                        this.listeners.set(queryToken, new Set([setState]));
                    }
                    return () => {
                        const currentListeners = this.listeners.get(queryToken);
                        currentListeners.delete(setState);
                        if (currentListeners.size == 0) {
                            this.listeners.delete(queryToken);
                        }
                        unsubscribe();
                    };
                    // If the args change, new query. If name changes, that's a new hook.
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                }, [JSON.stringify(convexToJson(args))]);
                return state;
            },
            watch: (...args) => {
                return {
                    onUpdate: (update) => {
                        const { queryToken, unsubscribe } = this.sync.subscribe(name, args);
                        const currentListeners = this.listeners.get(queryToken);
                        if (currentListeners !== undefined) {
                            currentListeners.add(update);
                        }
                        else {
                            this.listeners.set(queryToken, new Set([update]));
                        }
                        return () => {
                            const currentListeners = this.listeners.get(queryToken);
                            currentListeners.delete(update);
                            if (currentListeners.size == 0) {
                                this.listeners.delete(queryToken);
                            }
                            unsubscribe();
                        };
                    },
                };
            },
        };
    }
    /**
     * Construct a new handle to a `Mutation`.
     *
     * @param name - The name of the Mutation.
     * @returns The `Mutation` object with that name.
     */
    mutation(name) {
        return new MutationImpl(name, this.sync);
    }
    /**
     * Close any network handles associated with this client.
     *
     * Call this method when you're done with a {@link ReactClient} to
     * dispose of its sockets and resources.
     *
     * @returns A `Promise` fulfilled when the connection has been completely closed.
     */
    async close() {
        if (this.cachedSync) {
            const sync = this.cachedSync;
            this.cachedSync = undefined;
            await sync.close();
        }
    }
    transition(updates) {
        ReactDOM.unstable_batchedUpdates(() => {
            for (const [queryId, value] of updates) {
                const callbacks = this.listeners.get(queryId);
                if (callbacks) {
                    for (const callback of callbacks) {
                        callback(value);
                    }
                }
            }
        });
    }
}
/**
 * @public
 *
 * React hook for data binding to a query's output.
 *
 * @param query - The `ReactQuery` object to invoke.
 * @param args - Arguments to pass to the query.
 */
export function useQuery(query, ...args) {
    return query.useSubscription(...args);
}
//# sourceMappingURL=index.js.map